// src/pages/.../EditNewsResponsablesModal.jsx
import React, { useState, useEffect } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { updateNews } from "../../services/newsServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const formatDateForInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
};

const getInitialFormState = () => ({
  title: "",
  content: "",
  image_url: "",
  created_at: "",
  is_active: true,
  id: "",
  destinataires_news: "Responsables clubs",
});

const EditNewsModal = ({
  isOpen,
  onClose,
  refreshList,
  news,
  onEditSuccess,
}) => {
  const [formData, setFormData] = useState(getInitialFormState);

  useEffect(() => {
    if (news && isOpen) {
      const destinataires =
        news.destinataires_news ??
        news.destinaires_news ?? // fallback ancienne orthographe
        "Responsables clubs";

      setFormData({
        title: news.title || "",
        content: news.content || "",
        image_url: news.image_url || "",
        is_active: !!news.is_active,
        created_at: formatDateForInput(news.created_at),
        id: news.id,
        destinataires_news: destinataires,
      });
    }
  }, [news, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(getInitialFormState());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.id) {
      toast.error("Actualité introuvable.");
      return;
    }

    try {
      await updateNews({
        ...formData,
        // on renvoie bien destinataires_news côté API
      });

      if (onEditSuccess) {
        await onEditSuccess();
      } else if (refreshList) {
        await refreshList();
        toast.success("Actualité mise à jour avec succès");
      }

      onClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'actualité", error);
      toast.error("Erreur lors de la mise à jour de l'actualité.");
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
      <div className="relative w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200/60 dark:border-slate-700/60">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-slate-200/70 dark:border-slate-700/70 bg-gradient-to-r from-primary to-primary/80 text-white rounded-t-2xl">
          <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide font-montserrat">
            Mettre à jour une actualité
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-sm"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          className="px-4 sm:px-6 py-4 space-y-6 text-slate-800 dark:text-slate-100"
        >
          {/* Section infos générales */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Informations générales
            </h4>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold">
                Titre de l’actualité
              </label>
              <input
                type="text"
                name="title"
                placeholder="Titre de l'actualité"
                value={formData.title}
                onChange={handleChange}
                className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                required
              />
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold">
                  Destinataires
                </label>
                <select
                  name="destinataires_news"
                  value={formData.destinataires_news}
                  onChange={handleChange}
                  className="select select-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                >
                  <option value="Tout public">Tout public</option>
                  <option value="Responsables clubs">
                    Responsables clubs
                  </option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold">
                  Date de publication (affichage)
                </label>
                <input
                  type="date"
                  name="created_at"
                  value={formData.created_at}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                />
              </div>

              <div className="flex items-center gap-2 mt-2 md:mt-6">
                <input
                  type="checkbox"
                  id="is_active_edit"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="form-checkbox h-4 w-4 text-primary rounded"
                />
                <label
                  htmlFor="is_active_edit"
                  className="text-xs sm:text-sm text-slate-700 dark:text-slate-200"
                >
                  Actualité active (visible)
                </label>
              </div>
            </div>
          </div>

          {/* Section contenu */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Contenu du message
            </h4>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
              <ReactQuill
                value={formData.content}
                onChange={handleContentChange}
                className="quill-editor"
              />
            </div>
          </div>

          {/* Section image */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Illustration (optionnelle)
            </h4>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold">
                URL de l’image
              </label>
              <input
                type="url"
                name="image_url"
                placeholder="https://..."
                value={formData.image_url}
                onChange={handleChange}
                className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-3 pt-2 pb-4">
            <Button
              text="Mettre à jour"
              icon={FaSave}
              type="submit"
              className="btn-primary gap-2"
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={handleCancel}
              type="button"
              className="btn-ghost gap-2"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditNewsModal;
